import {
	type PenaltyBox,
	type JwksCache,
	type JwkWithKid,
	type Jwks,
	assertIsJwks,
	isJwks,
	findJwkInJwks,
	SimplePenaltyBox,
} from "aws-jwt-verify/jwk";
import {
	JwtWithoutValidKidError,
	JwksNotAvailableInCacheError,
	KidNotFoundInJwksError,
} from "aws-jwt-verify/error";

type DecomposedJwt = Parameters<JwksCache["getJwk"]>[1];

function createLocalStorageJwksCache(storage: Storage): JwksCache {
	const penaltyBox: PenaltyBox = new SimplePenaltyBox();

	function getLocalStorageKey(jwksUri: string) {
		return `jwks.${jwksUri}`;
	}

	function readJwks(jwksUri: string) {
		const raw = storage.getItem(getLocalStorageKey(jwksUri));
		if (raw === null) {
			return undefined;
		}

		try {
			const possibleJwks = JSON.parse(raw);
			if (isJwks(possibleJwks)) {
				return possibleJwks;
			}
		} catch {
			return undefined;
		}

		return undefined;
	}

	function writeJwks(jwksUri: string, jwks: Jwks) {
		storage.setItem(getLocalStorageKey(jwksUri), JSON.stringify(jwks));
	}

	return {
		async getJwk(
			jwksUri: string,
			decomposedJwt: DecomposedJwt,
		): Promise<JwkWithKid> {
			if (typeof decomposedJwt.header.kid !== "string") {
				throw new JwtWithoutValidKidError(
					"JWT header does not have valid kid claim",
				);
			}

			// Try to get JWK from cache:
			const storedJwks = readJwks(jwksUri);
			if (storedJwks !== undefined) {
				const cachedJwk = findJwkInJwks(storedJwks, decomposedJwt.header.kid);
				if (cachedJwk !== undefined) {
					return cachedJwk;
				}
			}

			// Await any wait period that is currently in effect
			// This prevents us from flooding the JWKS URI with requests
			await penaltyBox.wait(jwksUri, decomposedJwt.header.kid);

			// Fetch the JWKS and (try to) locate the JWK
			const jwks = await this.getJwks(jwksUri);
			const jwk = findJwkInJwks(jwks, decomposedJwt.header.kid);

			// If the JWK could not be located, someone might be messing around with us
			// Register the failed attempt with the penaltyBox, so it can enforce a wait period
			// before trying again next time (instead of flooding the JWKS URI with requests)
			if (jwk === undefined) {
				penaltyBox.registerFailedAttempt(jwksUri, decomposedJwt.header.kid);
				throw new KidNotFoundInJwksError(
					`JWK for kid "${decomposedJwt.header.kid}" not found in the JWKS`,
				);
			} else {
				penaltyBox.registerSuccessfulAttempt(jwksUri, decomposedJwt.header.kid);
			}

			return jwk;
		},
		getCachedJwk(jwksUri: string, decomposedJwt: DecomposedJwt): JwkWithKid {
			if (typeof decomposedJwt.header.kid !== "string") {
				throw new JwtWithoutValidKidError(
					"JWT header does not have valid kid claim",
				);
			}

			const storedJwks = readJwks(jwksUri);
			if (storedJwks === undefined) {
				throw new JwksNotAvailableInCacheError(
					`JWKS for uri ${jwksUri} not yet available in cache`,
				);
			}

			const jwk = findJwkInJwks(storedJwks, decomposedJwt.header.kid);
			if (jwk === undefined) {
				throw new KidNotFoundInJwksError(
					`JWK for kid ${decomposedJwt.header.kid} not found in the JWKS`,
				);
			}

			return jwk;
		},
		addJwks: writeJwks,
		async getJwks(jwksUri: string): Promise<Jwks> {
			const storedJwks = readJwks(jwksUri);
			if (storedJwks !== undefined) {
				return storedJwks;
			}

			const response = await fetch(jwksUri);
			// fetch only rejects on network failure, so guard against HTTP errors
			// before parsing - otherwise an HTML error page surfaces as a confusing
			// JSON parse / assertIsJwks failure instead of the real status.
			if (!response.ok) {
				throw new Error(
					`Failed to fetch JWKS from ${jwksUri}: ${response.status} ${response.statusText}`,
				);
			}
			const responseObject = await response.json();
			assertIsJwks(responseObject);
			writeJwks(jwksUri, responseObject);
			return responseObject;
		},
	};
}

export { createLocalStorageJwksCache };
